import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectCreative, EffectFade, Autoplay, Pagination, Navigation } from 'swiper/modules'
import { motion } from 'framer-motion'
import { Users, GraduationCap, Building2, BookOpen, UsersRound, Globe2 } from 'lucide-react'
import Button from '../ui/Button'
import MediaImage from '../ui/MediaImage'
import { resolveMediaUrl, mediaFallback } from '../../utils/media'
import { slidersApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { useIsMobile } from '../../hooks/useMediaQuery'
import { useHomepageSection } from '../../hooks/useHomepageSection'
import { HOMEPAGE_HERO_SLIDES } from '../../constants/homepageHeroSlides'
import { normalizeHeroStats } from '../../constants/homepageAdminConfig'

import 'swiper/css'
import 'swiper/css/effect-creative'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

const FALLBACK_SLIDES = HOMEPAGE_HERO_SLIDES.map((slide, index) => ({
  id: index + 1,
  ...slide,
}))

const STAT_ICONS = {
  students: Users,
  years: GraduationCap,
  campuses: Building2,
  programs: BookOpen,
  staff: UsersRound,
  alumni: Globe2,
}

const HERO_KEYS = [
  { title: 'hero.slide1Title', subtitle: 'hero.slide1Subtitle' },
  { title: 'hero.slide2Title', subtitle: 'hero.slide2Subtitle' },
  { title: 'hero.slide3Title', subtitle: 'hero.slide3Subtitle' },
  { title: 'hero.slide4Title', subtitle: 'hero.slide4Subtitle' },
]

export default function HeroSlider() {
  const { t, i18n } = useTranslation()
  const { localized, isRTL } = useLanguage()
  const isMobile = useIsMobile(768)
  const cmsStats = useHomepageSection('hero_stats')
  const [slides, setSlides] = useState(FALLBACK_SLIDES)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    setActiveIndex(0)
  }, [i18n.language])

  useEffect(() => {
    slidersApi
      .getAll()
      .then(({ data }) => {
        const items = data.data || data
        if (Array.isArray(items) && items.length > 0) {
          setSlides(items)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const slide = slides[0]
    if (!slide) return undefined

    const fallback = FALLBACK_SLIDES[0]
    const rawImage = slide.image || slide.image_url
    const image = rawImage
      ? resolveMediaUrl(rawImage, null)
      : resolveMediaUrl(fallback?.image, mediaFallback(0))

    if (!image) return undefined

    const existing = document.querySelector('link[data-hero-preload="1"]')
    existing?.remove()

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = image
    link.setAttribute('data-hero-preload', '1')
    document.head.appendChild(link)

    return () => {
      link.remove()
    }
  }, [slides])

  const getSlideContent = (slide, index) => {
    const fallback = FALLBACK_SLIDES[index] || FALLBACK_SLIDES[0]
    const label = localized(slide, 'label') || localized(fallback, 'label') || t('brand.tagline')
    const title = localized(slide, 'title') || t(HERO_KEYS[index]?.title || 'hero.slide1Title')
    const subtitle =
      localized(slide, 'subtitle') ||
      t(HERO_KEYS[index]?.subtitle || 'hero.slide1Subtitle')
    const rawImage = slide.image || slide.image_url
    const image = rawImage
      ? resolveMediaUrl(rawImage, null)
      : resolveMediaUrl(fallback?.image, mediaFallback(index))
    return { label, title, subtitle, image: image || mediaFallback(index) }
  }

  const swiperModules = isMobile
    ? [EffectFade, Autoplay, Pagination]
    : [EffectCreative, Autoplay, Pagination, Navigation]

  const heroStats = normalizeHeroStats(cmsStats.settings?.stats).map((stat) => {
    const isAr = i18n.language === 'ar'
    const cmsLabel = isAr ? stat.label_ar || stat.label_en : stat.label_en || stat.label_ar
    return {
      key: stat.key,
      value: stat.value,
      icon: STAT_ICONS[stat.key] || Users,
      label: cmsLabel || t(`homeStats.items.${stat.key}`),
    }
  })

  const activeSlide = slides[activeIndex] || slides[0]
  const videoSrc = activeSlide?.video_file || activeSlide?.video_url

  return (
    <div className="relative mb-6 sm:mb-20 lg:mb-28">
      <section className="relative min-h-[min(100dvh,900px)] h-[85dvh] sm:h-screen sm:min-h-[560px] sm:max-h-[900px] w-full overflow-hidden bg-primary">
        {videoSrc ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={getSlideContent(activeSlide, activeIndex).image}
            className="absolute inset-0 h-full w-full object-cover"
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        ) : null}

        <Swiper
          key={`hero-${i18n.language}-${isMobile ? 'mobile' : 'desktop'}`}
          dir={isRTL ? 'rtl' : 'ltr'}
          modules={swiperModules}
          effect={isMobile ? 'fade' : 'creative'}
          grabCursor
          creativeEffect={
            isMobile
              ? undefined
              : {
                  prev: {
                    shadow: true,
                    translate: ['-18%', 0, -200],
                    opacity: 0,
                  },
                  next: {
                    translate: ['100%', 0, 0],
                    opacity: 0,
                  },
                }
          }
          fadeEffect={{ crossFade: true }}
          autoplay={{ delay: 5500, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation={!isMobile}
          loop
          onSwiper={(swiper) => setActiveIndex(swiper.realIndex)}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          className={`h-full w-full !pb-14 sm:!pb-16 ${videoSrc ? 'opacity-0' : 'opacity-100'}`}
        >
          {slides.map((slide, index) => {
            const { title, image } = getSlideContent(slide, index)
            return (
              <SwiperSlide key={`${slide.id || index}-${i18n.language}`} className="!h-full !w-full">
                <div className="relative h-full w-full">
                  <MediaImage
                    src={image}
                    alt={title}
                    fallbackIndex={index}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/60 to-primary/30 sm:from-primary/90 sm:via-primary/50 sm:to-primary/20" />
                  <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-primary/40 via-primary/10 to-transparent sm:h-40" />
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>

        <div className="pointer-events-none absolute inset-0 z-10 flex items-end pb-14 sm:items-center sm:pb-0">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="pointer-events-auto relative z-30 max-w-2xl sm:header-offset sm:pb-0"
            >
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-3 inline-block rounded-full bg-accent-strong/90 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white sm:mb-4 sm:px-4 sm:py-1.5 sm:text-xs"
              >
                {getSlideContent(slides[activeIndex], activeIndex).label}
              </motion.span>
              <h1 className="mb-3 text-3xl font-bold leading-tight text-white sm:mb-4 sm:text-4xl md:text-5xl lg:text-6xl">
                {getSlideContent(slides[activeIndex], activeIndex).title}
              </h1>
              <p className="mb-4 max-w-xl text-base leading-relaxed text-white/90 sm:mb-8 sm:text-lg md:text-xl">
                {getSlideContent(slides[activeIndex], activeIndex).subtitle}
              </p>
              <div className="relative z-30 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 md:gap-4">
                <Button
                  to="/apply"
                  variant="accent"
                  size="sm"
                  className="col-span-1 w-full px-3 py-2.5 text-xs sm:w-auto sm:px-8 sm:py-4 sm:text-base"
                >
                  {t('buttons.applyNow')}
                </Button>
                <Button
                  to="/programs"
                  variant="primary"
                  size="sm"
                  className="col-span-1 w-full bg-white/15 px-3 py-2.5 text-xs backdrop-blur-sm hover:bg-white/25 sm:w-auto sm:px-8 sm:py-4 sm:text-base"
                >
                  {t('buttons.findProgram')}
                </Button>
                <Button
                  to="/contact"
                  variant="outline"
                  size="sm"
                  className="col-span-2 w-full px-3 py-2.5 text-xs sm:col-span-1 sm:w-auto sm:px-8 sm:py-4 sm:text-base"
                >
                  {t('buttons.contactUs')}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="relative z-20 mt-4 px-2 sm:absolute sm:bottom-0 sm:left-0 sm:right-0 sm:mt-0 sm:translate-y-1/2 sm:px-0">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-6">
            {heroStats.map(({ key, value, icon: Icon, label }) => (
              <div
                key={key}
                className="rounded-xl border border-border/60 bg-white p-3 text-center shadow-xl shadow-primary/10 sm:rounded-2xl sm:p-4"
              >
                <div className="mx-auto mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 sm:mb-2 sm:h-10 sm:w-10 sm:rounded-xl">
                  <Icon className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                </div>
                <div className="text-xl font-bold text-primary sm:text-2xl md:text-3xl">{value}</div>
                <div className="mt-0.5 text-[10px] text-text/60 sm:mt-1 sm:text-xs md:text-sm">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
